import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CardForm from '../../components/CardForm';
import AddressForm from '../../components/AddressForm';
import TermsAndSubmit from '../../components/TermsAndSubmit';
import LoadingComponent from '../../components/Loading';
import B2b from '../api/b2b';
import Pagarme from '../api/pagarme';
import { ROUTES } from '../constants/routes';
import { useUser } from '../../hooks/useUser';
import { formatBRL } from '../utils/formatBRL';
import { isPaymentFormValid } from '../utils/isPaymentFormValid';

const PackagePayment = () => {
  const { currentUser } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const pkg = location.state?.package;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '', focus: '' });
  const [cardErrors, setCardErrors] = useState({});
  const [addressErrors, setAddressErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [address, setAddress] = useState({
    zip_code: '', street: '', number: '', complement: '',
    neighborhood: '', city: '', state: ''
  });

  const cardFormRef = useRef(null);
  const addressFormRef = useRef(null);

  useEffect(() => {
    if (currentUser?.last_billing_address) {
      setAddress({
        zip_code: currentUser.last_billing_address.zip_code || '',
        street: currentUser.last_billing_address.address || '',
        number: currentUser.last_billing_address.number || '',
        complement: currentUser.last_billing_address.complement || '',
        neighborhood: currentUser.last_billing_address.neighborhood || '',
        city: currentUser.last_billing_address.city || '',
        state: currentUser.last_billing_address.state || ''
      });
    }
  }, [currentUser]);

  if (!pkg) return <LoadingComponent />;

  const handlePaymentSubmit = async () => {
    try {
      const isCardValid = await cardFormRef.current?.validate();
      const isAddressValid = await addressFormRef.current?.validate();
      if (!isCardValid || !isAddressValid || !termsAccepted) return;

      setIsSubmitting(true);
      setErrorMessage('');

      const cardToken = await Pagarme.createCardToken(card);

      const response = await B2b.buyPackage(pkg.id, {
        card_token: cardToken,
        price: (pkg.price_cents / 100).toFixed(2),
        address: {
          zip_code: address.zip_code.replace(/\D/g, ''),
          street: address.street,
          number: address.number,
          complement: address.complement || '',
          neighborhood: address.neighborhood,
          city: address.city,
          state: address.state
        }
      });

      if (response?.payment?.status === 'paid') {
        navigate(ROUTES.MY_INSPECTIONS, {
          state: { success: `Pacote de ${pkg.credits_quantity} consultas creditado com sucesso!` }
        });
      } else {
        throw new Error(response?.error || 'Pagamento não aprovado');
      }
    } catch (error) {
      const msg = error?.data?.error || error?.error || error.message || 'Erro ao processar o pagamento';
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () =>
    isPaymentFormValid({
      card,
      address,
      cardErrors,
      addressErrors,
      termsAccepted,
      isSubmitting,
    });

  return (
    <Box sx={{ bgcolor: '#F5F7F9', minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 6, px: { xs: 2, md: 4 } }}>
      <Typography variant="h1" sx={{ textAlign: 'center', color: '#012F47', fontWeight: 700, fontSize: { xs: 24, md: 32 }, mb: 3 }}>
        Comprar pacote
      </Typography>

      <Box sx={{ maxWidth: 480, mx: 'auto', mb: 3, p: 2.5, borderRadius: 3, bgcolor: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#012F47' }}>{pkg.credits_quantity}</Typography>
        <Typography variant="body2" color="text.secondary">consultas</Typography>
        <Typography variant="h6" sx={{ color: '#00B26C', fontWeight: 700 }}>{formatBRL(pkg.price_cents)}</Typography>
      </Box>

      {errorMessage ? <Alert severity="error" sx={{ maxWidth: 480, mx: 'auto', mb: 2 }}>{errorMessage}</Alert> : null}

      <CardForm ref={cardFormRef} card={card} setCard={setCard} errors={cardErrors} setErrors={setCardErrors} />
      <AddressForm ref={addressFormRef} address={address} setAddress={setAddress} errors={addressErrors} setErrors={setAddressErrors} currentUser={currentUser} />
      <TermsAndSubmit
        checked={termsAccepted}
        onCheckChange={(e) => setTermsAccepted(e.target.checked)}
        onSubmit={handlePaymentSubmit}
        disabled={!isFormValid()}
        isLoading={isSubmitting}
      />
    </Box>
  );
};

export default PackagePayment;
